import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { GraduationCap, MapPin, ArrowRight, FileText, Building2 } from 'lucide-react';
import { InternshipCard } from './InternshipDirectory';
import { useJsonLd, SITE_URL } from './useJsonLd';
import { STATE_MAP } from './stateData';

const StateInternships = () => {
  const { state: slug } = useParams();
  const info = STATE_MAP[slug];
  const [internships, setInternships] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!info) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/internships`, { params: { state: info.name } });
      setInternships(res.data.internships || []);
      setTotal(res.data.total || 0);
    } catch (e) { setInternships([]); } finally { setLoading(false); }
  }, [info]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!info) return;
    document.title = `MSW & BSW Internships in ${info.name} | Social Work Field Placements | I Need A Social Worker`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = `Find MSW and BSW social work internships and field placements in ${info.name}. Browse generalist and clinical field placements near you.`;
  }, [info]);

  const faqs = info ? [
    { q: `How do I find a social work field placement in ${info.name}?`, a: `Browse MSW and BSW internships on I Need A Social Worker, filter by concentration and format, and apply with a free account to field placements across ${info.name}.` },
    { q: `Are MSW clinical internships available in ${info.name}?`, a: `Yes. Listings include both generalist and clinical (MSW) concentrations at agencies and programs throughout ${info.name}.` },
    { q: `Do these internships include supervision?`, a: `Most field placements in ${info.name} include qualified supervision — each listing notes whether supervision is provided.` },
  ] : [];

  useJsonLd('state-internship-collection', info ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Social Work Internships in ${info.name}`,
    description: `Directory of BSW and MSW social work internships and field placements in ${info.name}.`,
    url: `${SITE_URL}/${slug}/msw-internships`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: internships.slice(0, 25).map((it, i) => ({
        '@type': 'ListItem', position: i + 1,
        url: `${SITE_URL}/internships/${it.internship_id}`,
        name: `${it.title} — ${it.org_name}`,
      })),
    },
  } : null);

  useJsonLd('state-internship-faq', info ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  } : null);

  if (!info) return <Navigate to="/internships" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="relative pt-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-white">
          <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> {info.name} · MSW & BSW Field Placements
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold" data-testid="state-internship-title">
            Social Work Internships in {info.name}
          </h1>
          <p className="text-white/90 mt-4 max-w-2xl text-base sm:text-lg">
            Discover MSW and BSW field placements across {info.name}. Gain supervised experience, build clinical skills, and launch your social work career.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/internships"><Button className="bg-secondary hover:bg-secondary-hover text-white" data-testid="state-view-all-internships">View all internships <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/post-an-internship"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary" data-testid="state-post-internship">Post an Internship</Button></Link>
          </div>
        </div>
      </section>

      {/* City chips */}
      <section className="bg-white border-b py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-500 mb-3">Field placements across {info.name}:</p>
          <div className="flex flex-wrap gap-2">
            {info.cities.map((c) => (
              <span key={c} className="px-4 py-2 bg-primary/5 border border-primary/15 rounded-lg text-primary text-sm font-medium" data-testid={`state-city-${c}`}>
                Internships in {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl font-bold text-primary">Internships in {info.name}</h2>
          <span className="text-sm text-gray-500" data-testid="state-internship-count">{loading ? 'Loading…' : `${total} posted`}</span>
        </div>

        {!loading && internships.length === 0 ? (
          <div className="bg-white rounded-2xl border p-10 text-center" data-testid="state-no-internships">
            <GraduationCap className="w-10 h-10 text-secondary mx-auto mb-3" />
            <h3 className="font-serif text-xl text-primary font-bold mb-1">Internships in {info.name} are coming soon</h3>
            <p className="text-gray-500 mb-5">New field placements are added regularly. Are you an agency or school in {info.name}? Post an internship to reach motivated students.</p>
            <Link to="/post-an-internship"><Button className="bg-primary text-white">Post an Internship</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {internships.map((it) => <InternshipCard key={it.internship_id} it={it} />)}
          </div>
        )}
      </section>

      {/* Why + FAQ */}
      <section className="bg-white border-t py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <Feature icon={FileText} title="Easy to Apply" text="Apply to field placements with a free account." />
            <Feature icon={Building2} title="Trusted Programs" text={`Reputable agencies and schools across ${info.name}.`} />
            <Feature icon={GraduationCap} title="BSW & MSW" text="Generalist and clinical concentrations." />
          </div>
          <h2 className="font-serif text-2xl font-bold text-primary mb-6">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {faqs.map((f) => (
              <div key={f.q} data-testid="state-internship-faq-item">
                <p className="font-semibold text-gray-900">{f.q}</p>
                <p className="text-gray-600 mt-1">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const Feature = ({ icon: Icon, title, text }) => (
  <div className="text-center">
    <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3"><Icon className="w-5 h-5 text-primary" /></div>
    <p className="font-semibold text-primary">{title}</p>
    <p className="text-sm text-gray-500 mt-1">{text}</p>
  </div>
);

export default StateInternships;
